import { useToast, type ToastTone } from "@/contexts/ToastContext";
import { useT } from "@/contexts/I18nContext";

/**
 * The notification stack.
 *
 * `role="status"` with `aria-live="polite"` so a screen reader announces a
 * result without interrupting whatever is being read. Errors use `alert`,
 * because "that did not save" is worth interrupting for.
 */

const TONES: Record<ToastTone, string> = {
  success: "border-cue-green bg-cue-green-soft text-ink",
  error: "border-accent bg-accent-soft text-ink",
  info: "border-paper-deep bg-white text-ink",
};

export function ToastStack() {
  const { toasts, dismiss } = useToast();
  const t = useT();

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.tone === "error" ? "alert" : "status"}
          className={`pointer-events-auto flex items-start gap-3 rounded-md border px-4 py-3
                      text-sm shadow-sm ${TONES[toast.tone]}`}
        >
          <span className="flex-1">{t(toast.key, toast.params)}</span>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label={t("common.close")}
            className="text-ink-muted hover:text-ink"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}
